import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { emailCronConfigActions } from '../../../../store';

import type { AppDispatch, RootState } from '../../../../store';
import type {
  IEmailCronConfigPayload,
  IEmailCronConfigPreviewPayload,
} from '../../../../utils/types';

// Wraps all email-cron-config Redux reads/dispatches for the details page so the
// component never touches react-redux or the slice directly.
export const useEmailCronConfigDetails = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();

  const current = useSelector((s: RootState) => s.emailCronConfigs.current);
  const preview = useSelector((s: RootState) => s.emailCronConfigs.preview);
  const previewConfigId = useSelector((s: RootState) => s.emailCronConfigs.previewConfigId);

  const loadConfig = useCallback(
    (id: string) =>
      dispatch(emailCronConfigActions.getById({ id, getAccessTokenSilently })),
    [dispatch, getAccessTokenSilently],
  );

  const clearConfig = useCallback(() => {
    dispatch(emailCronConfigActions.clearCurrent());
    dispatch(emailCronConfigActions.clearPreview());
  }, [dispatch]);

  const saveConfig = useCallback(
    (id: string, data: IEmailCronConfigPayload) =>
      dispatch(emailCronConfigActions.update({ id, data, getAccessTokenSilently })),
    [dispatch, getAccessTokenSilently],
  );

  const previewConfig = useCallback(
    (id: string, data: IEmailCronConfigPreviewPayload) =>
      dispatch(emailCronConfigActions.preview({ id, data, getAccessTokenSilently })),
    [dispatch, getAccessTokenSilently],
  );

  const removeConfig = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await dispatch(
          emailCronConfigActions.remove({ id, getAccessTokenSilently }),
        ).unwrap();
        return true;
      } catch {
        // Error toast handled by the slice's rejected matcher.
        return false;
      }
    },
    [dispatch, getAccessTokenSilently],
  );

  return {
    current,
    preview,
    previewConfigId,
    loadConfig,
    clearConfig,
    saveConfig,
    previewConfig,
    removeConfig,
  };
};
